import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './AdminPage.css';

const API     = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/admin`;
const API_KW  = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/keywords`;

interface JobRunInfo {
  triggeredBy: string | null;
  startedAt:   string | null;
  completedAt: string | null;
  status:      string | null;
}

interface JobProgress {
  found:                     boolean;
  jobId?:                    string;
  status?:                   string;
  currentStep?:              string | null;
  totalRecords?:             number;
  processedRecords?:         number;
  percentComplete?:          number;
  startedAt?:                string | null;
  lastUpdatedAt?:             string | null;
  elapsedSeconds?:            number;
  estimatedSecondsRemaining?: number | null;
  estimatedEndAt?:            string | null;
}

interface Job {
  id:            string;
  name:          string;
  description:   string;
  icon:          string;
  running:       boolean;
  lastRun:       string | null;
  lastStatus:    string | null;
  lastScheduled: JobRunInfo | null;
  lastManual:    JobRunInfo | null;
}

interface Config {
  id:           number;
  key:          string;
  value:        string;
  description:  string;
  category:     string;
  editable:     boolean;
  lastModified: string;
}

interface User {
  userId:      number;
  username:    string;
  email:       string;
  firstName:   string;
  lastName:    string;
  dateOfBirth: string | null;
  role:        string;
  active:      boolean;
  createdDate: string | null;
  lastLogin:   string | null;
  loginCount:  number;
}

interface Keyword {
  id:                 number;
  keyword:            string;
  definition:         string;
  originalDefinition: string;
  source:             string;
  active:             boolean;
  lastModified:       string;
}

const ROLES = ['ADMIN','USER'];
const SOURCES = ['all', 'comprehensive_rules', 'ability_word', 'crossover', 'un_set', 'manual'];

// Config field type definitions
type ConfigFieldType = 'text' | 'dropdown' | 'multiselect' | 'boolean' | 'sets';
interface ConfigField {
  type: ConfigFieldType;
  options?: { value: string; label: string }[];
}

const LANG_OPTIONS = [
  { value: 'en',  label: 'English' },
  { value: 'ja',  label: 'Japanese' },
  { value: 'fr',  label: 'French' },
  { value: 'de',  label: 'German' },
  { value: 'es',  label: 'Spanish' },
  { value: 'it',  label: 'Italian' },
  { value: 'zhs', label: 'Chinese Simplified' },
  { value: 'pt',  label: 'Portuguese' },
  { value: 'zht', label: 'Chinese Traditional' },
  { value: 'ru',  label: 'Russian' },
  { value: 'ko',  label: 'Korean' },
  { value: 'ph',  label: 'Phyrexian' },
  { value: 'qya', label: 'Quenya (Elvish)' },
  { value: 'grc', label: 'Ancient Greek' },
  { value: 'ar',  label: 'Arabic' },
  { value: 'he',  label: 'Hebrew' },
  { value: 'la',  label: 'Latin' },
  { value: 'sa',  label: 'Sanskrit' },
];

const SET_TYPE_OPTIONS = [
  'expansion','core','masters','commander','draft_innovation','duel_deck',
  'from_the_vault','masterpiece','premium_deck','arsenal','planechase',
  'archenemy','box','starter','funny','alchemy','minigame','memorabilia',
  'token','promo','eternal','spellbook','treasure_chest','vanguard',
].map(v => ({ value: v, label: v.replace(/_/g, ' ') }));

const IMAGE_TYPE_OPTIONS = ['SMALL','NORMAL','LARGE','PNG','ART_CROP','BORDER_CROP']
  .map(v => ({ value: v, label: v }));

const CONFIG_FIELDS: Record<string, ConfigField> = {
  'price.comparison.mode':          { type: 'dropdown',     options: ['LATEST','WEEKLY','MONTHLY'].map(v=>({value:v,label:v})) },
  'alert.reserved.comparison.mode': { type: 'dropdown',     options: ['LATEST','WEEKLY','MONTHLY'].map(v=>({value:v,label:v})) },
  'images.downloadTypes':           { type: 'multiselect',  options: IMAGE_TYPE_OPTIONS },
  'images.downloadLanguages':       { type: 'multiselect',  options: LANG_OPTIONS },
  'sync.includeLanguages':          { type: 'multiselect',  options: LANG_OPTIONS },
  'sync.excludeSetTypes':           { type: 'multiselect',  options: SET_TYPE_OPTIONS },
  'images.skipDigitalSets':         { type: 'boolean' },
  'images.downloadSets':             { type: 'sets' },
};

// Human-readable category labels and icons
const CATEGORY_META: Record<string, { label: string; icon: string; description: string }> = {
  email:    { label: 'Email',          icon: '✉',  description: 'Automated job email settings' },
  features: { label: 'Features',       icon: '🚩', description: 'Feature flags' },
  market:   { label: 'Market',         icon: '📈', description: 'Market movers report settings' },
  pricing:  { label: 'Pricing',        icon: '💰', description: 'Price brackets and report settings' },
  spotlight:{ label: 'Daily Spotlight',icon: '🔦', description: 'Card spotlight email settings' },
  system:   { label: 'System',         icon: '⚙',  description: 'Sync engine and API settings' },
};

function fmtJobDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function fmtTimeOnly(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function computeRunDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt || !completedAt) return '';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0 || isNaN(ms)) return '';
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hours}h ${remMins}m`;
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hours}h ${remMins}m`;
}

function formatDob(d: string | null) {
  if (!d) return '—';
  // Convert YYYY-MM-DD to DD/MM/YYYY
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
}

// Download Sets editor — ALL_SETS is the default; add codes to override
function DownloadSetsEditor({
  value, onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const isAllSets = !value || value.trim() === '' || value.trim().toUpperCase() === 'ALL_SETS';
  const codes = isAllSets ? [] : value.split(',').map(s => s.trim()).filter(Boolean);
  const [input, setInput] = React.useState('');

  const addCode = () => {
    const code = input.trim().toLowerCase();
    if (!code) return;
    const next = [...codes.filter(c => c !== code), code];
    onChange(next.join(','));
    setInput('');
  };

  const removeCode = (code: string) => {
    const next = codes.filter(c => c !== code);
    // Revert to ALL_SETS when last code is removed
    onChange(next.length === 0 ? 'ALL_SETS' : next.join(','));
  };

  const setAllSets = () => { onChange('ALL_SETS'); setInput(''); };

  return (
    <div className="config-sets-editor">
      {/* ALL_SETS indicator */}
      <div className="config-multiselect" style={{marginBottom: 6}}>
        <button type="button"
          className={`config-badge ${isAllSets ? 'active' : 'all-sets-inactive'}`}
          onClick={setAllSets}
          title={isAllSets ? 'Currently syncing all sets' : 'Click to revert to all sets'}>
          ALL_SETS {isAllSets
            ? <span style={{color:'#2ecc71', fontSize:10}}>✓</span>
            : <span style={{color:'#505070', fontSize:10}}>← revert</span>}
        </button>
        {/* Individual set code badges */}
        {codes.map(code => (
          <button key={code} type="button" className="config-badge active"
                  onClick={() => removeCode(code)}
                  title="Click to remove">
            {code} <span className="config-badge-x">✕</span>
          </button>
        ))}
      </div>
      {/* Always show input so user can add set codes */}
      <div className="config-sets-input-row">
        <input
          className="config-input"
          style={{flex:1, maxWidth: 220}}
          placeholder={isAllSets ? 'e.g. blb, mh3' : 'Add set code'}
          value={input}
          onChange={e => setInput(e.target.value.toLowerCase())}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCode(); }}}
        />
        <button type="button" className="config-save-btn" onClick={addCode}
                disabled={!input.trim()}>Add</button>
      </div>
      {isAllSets && (
        <div style={{fontSize:11, color:'#404060', marginTop:4, fontFamily:'Outfit,sans-serif'}}>
          Enter a set code above to sync specific sets instead of all sets
        </div>
      )}
    </div>
  );
}

// Multi-select badge editor component
function MultiSelectBadgeEditor({
  value, options, onChange
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  const selected = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter(s => s !== opt)
      : [...selected, opt];
    onChange(next.join(','));
  };

  return (
    <div className="config-multiselect">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={`config-badge ${selected.includes(opt.value) ? 'active' : ''}`}
          onClick={() => toggle(opt.value)}
        >
          {opt.label}
          {selected.includes(opt.value) && <span className="config-badge-x">✕</span>}
        </button>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };
  const locationState = location.state as any;

  const [tab,     setTab]     = useState<'jobs'|'config'|'users'|'keywords'>(
    locationState?.tab || 'jobs'
  );
  const [jobs,    setJobs]    = useState<Job[]>([]);
  const [configs, setConfigs] = useState<Config[]>([]);
  const [users,   setUsers]   = useState<User[]>([]);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [kwSearch,       setKwSearch]       = useState('');
  const [kwFilter,       setKwFilter]       = useState<'all'|'active'|'inactive'>('active');
  const [kwSourceFilter, setKwSourceFilter] = useState<string>('all');
  const [kwSort,         setKwSort]         = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'keyword', dir: 'asc' });
  const [editKw,    setEditKw]    = useState<Keyword | null>(null);
  const [kwSaving,  setKwSaving]  = useState(false);
  const [jobOutput, setJobOutput] = useState<{ job: string; output: string } | null>(null);
  const [jobProgress, setJobProgress] = useState<Record<string, JobProgress>>({});
  const [editConfig, setEditConfig] = useState<{ key: string; value: string } | null>(null);
  const [editUser, setEditUser] = useState<User & { password?: string } | null>(null);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(locationState?.success || '');

  const fetchJobs    = useCallback(() =>
    axios.get(`${API}/jobs`).then(r => setJobs(r.data)).catch(console.error), []);
  const fetchConfigs = useCallback(() =>
    axios.get(`${API}/config`).then(r => setConfigs(r.data)).catch(console.error), []);
  const fetchUsers   = useCallback(() =>
    axios.get(`${API}/users`).then(r => setUsers(r.data)).catch(console.error), []);
  const fetchKeywords = useCallback(() =>
    axios.get(`${API_KW}?includeInactive=true`).then(r => setKeywords(r.data)).catch(console.error), []);
  const fetchJobProgress = useCallback((jobId: string) =>
    axios.get(`${API}/jobs/${jobId}/progress`)
      .then(r => setJobProgress(prev => ({ ...prev, [jobId]: r.data })))
      .catch(console.error), []);

  useEffect(() => { fetchJobs(); fetchConfigs(); fetchUsers(); fetchKeywords(); }, []);

  useEffect(() => {
    const running = jobs.filter(j => j.running);
    if (running.length === 0) return;
    const interval = setInterval(fetchJobs, 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  // Poll per-record progress for any currently running job (e.g. full-sync)
  useEffect(() => {
    const running = jobs.filter(j => j.running);
    if (running.length === 0) return;
    running.forEach(j => fetchJobProgress(j.id));
    const interval = setInterval(() => {
      running.forEach(j => fetchJobProgress(j.id));
    }, 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobProgress]);

  const runJob = async (jobId: string) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/run`);
      setSuccess(`Job started: ${jobId}`);
      setTimeout(() => setSuccess(''), 3000);
      fetchJobs();
      const poll = setInterval(async () => {
        const res = await axios.get(`${API}/jobs/${jobId}/status`);
        if (res.data.output) setJobOutput({ job: jobId, output: res.data.output });
        if (!res.data.running) clearInterval(poll);
      }, 3000);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to start job');
      setTimeout(() => setError(''), 4000);
    }
  };

  const saveConfig = async () => {
    if (!editConfig) return;
    try {
      await axios.put(`${API}/config/${editConfig.key}`, { value: editConfig.value });
      setEditConfig(null);
      setSuccess('Config saved');
      setTimeout(() => setSuccess(''), 3000);
      fetchConfigs();
    } catch (e) {
      setError('Failed to save config');
      setTimeout(() => setError(''), 4000);
    }
  };

  const deleteUser = async (userId: number, username: string) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      setSuccess('User deleted');
      setTimeout(() => setSuccess(''), 3000);
      fetchUsers();
    } catch (e) {
      setError('Cannot delete last admin');
      setTimeout(() => setError(''), 4000);
    }
  };

  const configsByCategory = configs.reduce((acc, cfg) => {
    if (!acc[cfg.category]) acc[cfg.category] = [];
    acc[cfg.category].push(cfg);
    return acc;
  }, {} as Record<string, Config[]>);

  const saveKeyword = async () => {
    if (!editKw) return;
    setKwSaving(true);
    try {
      await axios.put(`${API_KW}/${editKw.id}`, {
        definition: editKw.definition,
        source: editKw.source,
      });
      setEditKw(null);
      fetchKeywords();
    } catch (e) {
      setError('Failed to save keyword');
      setTimeout(() => setError(''), 4000);
    } finally { setKwSaving(false); }
  };

  const revertKeyword = async (kw: Keyword) => {
    setKwSaving(true);
    try {
      await axios.put(`${API_KW}/${kw.id}`, {
        definition: kw.originalDefinition,
        source: kw.source,
      });
      fetchKeywords();
      if (editKw?.id === kw.id) setEditKw(null);
    } catch (e) {
      setError('Failed to revert keyword');
      setTimeout(() => setError(''), 4000);
    } finally { setKwSaving(false); }
  };

  const toggleKeyword = async (kw: Keyword) => {
    try {
      await axios.put(`${API_KW}/${kw.id}`, {
        definition: kw.definition,
        source: kw.source,
        active: !kw.active,
      });
      fetchKeywords();
    } catch (e) {
      setError('Failed to update keyword');
      setTimeout(() => setError(''), 4000);
    }
  };

  const handleKwSort = (col: string) => {
    setKwSort(prev => prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' });
  };

  const sortIcon = (col: string) => {
    if (kwSort.col !== col) return ' ↕';
    return kwSort.dir === 'asc' ? ' ↑' : ' ↓';
  };

  const filteredKeywords = keywords
    .filter(kw => {
      const matchSearch = kw.keyword.toLowerCase().includes(kwSearch.toLowerCase()) ||
                          kw.definition.toLowerCase().includes(kwSearch.toLowerCase());
      const matchFilter = kwFilter === 'all' ? true : kwFilter === 'active' ? kw.active : !kw.active;
      const matchSource = kwSourceFilter === 'all' ? true : kw.source === kwSourceFilter;
      return matchSearch && matchFilter && matchSource;
    })
    .sort((a, b) => {
      const dir = kwSort.dir === 'asc' ? 1 : -1;
      switch (kwSort.col) {
        case 'keyword':    return dir * a.keyword.localeCompare(b.keyword);
        case 'definition': return dir * a.definition.localeCompare(b.definition);
        case 'source':     return dir * a.source.localeCompare(b.source);
        case 'status':     return dir * (Number(b.active) - Number(a.active));
        default:           return 0;
      }
    });

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1 className="admin-title">Admin Panel</h1>
      </div>

      {(error || success) && (
        <div className={`admin-toast ${error ? 'error' : 'success'}`}>
          {error || success}
        </div>
      )}

      <div className="admin-tabs">
        {(['jobs','config','users','keywords'] as const).map(t => (
          <button key={t} className={`admin-tab ${tab===t?'active':''}`}
                  onClick={() => setTab(t)}>
            {t === 'jobs'     ? '⚡ Jobs'
             : t === 'config' ? '⚙️ Config'
             : t === 'users'  ? '👥 Users'
             :                  '📖 Keywords'}
          </button>
        ))}
      </div>

      {/* ── JOBS ── */}
      {tab === 'jobs' && (
        <div className="admin-section">
          <div className="jobs-grid">
            {jobs.map(job => (
              <div key={job.id} className={`job-card ${job.running ? 'running' : ''}`}>
                <div className="job-icon">{job.icon}</div>
                <div className="job-info">
                  <div className="job-name">{job.name}</div>
                  <div className="job-desc">{job.description}</div>
                  <div className="job-run-history">
                    <div className="job-run-row">
                      <span className="job-run-type-label">Scheduled</span>
                      {job.lastScheduled ? (
                        <>
                          <span className="job-run-date">
                            Started {fmtJobDate(job.lastScheduled.startedAt)}
                          </span>
                          {job.lastScheduled.completedAt && (
                            <span className="job-run-date">
                              Finished {fmtJobDate(job.lastScheduled.completedAt)}
                              {computeRunDuration(job.lastScheduled.startedAt, job.lastScheduled.completedAt) &&
                                ` (${computeRunDuration(job.lastScheduled.startedAt, job.lastScheduled.completedAt)})`}
                            </span>
                          )}
                          <span className={`job-status ${job.lastScheduled.status}`}>{job.lastScheduled.status}</span>
                        </>
                      ) : <span className="job-run-never">never</span>}
                    </div>
                    <div className="job-run-row">
                      <span className="job-run-type-label">Manual</span>
                      {job.lastManual ? (
                        <>
                          <span className="job-run-date">
                            Started {fmtJobDate(job.lastManual.startedAt)}
                          </span>
                          {job.lastManual.completedAt && (
                            <span className="job-run-date">
                              Finished {fmtJobDate(job.lastManual.completedAt)}
                              {computeRunDuration(job.lastManual.startedAt, job.lastManual.completedAt) &&
                                ` (${computeRunDuration(job.lastManual.startedAt, job.lastManual.completedAt)})`}
                            </span>
                          )}
                          <span className={`job-status ${job.lastManual.status}`}>{job.lastManual.status}</span>
                          {job.lastManual.triggeredBy && (
                            <span className="job-run-by">by {job.lastManual.triggeredBy}</span>
                          )}
                        </>
                      ) : <span className="job-run-never">never</span>}
                    </div>
                  </div>
                  {job.running && jobProgress[job.id]?.found && (
                    <div className="job-progress-wrap">
                      <div className="job-progress-bar-track">
                        <div className="job-progress-bar-fill"
                             style={{ width: `${jobProgress[job.id].percentComplete ?? 0}%` }} />
                      </div>
                      <div className="job-progress-stats">
                        <span className="job-progress-pct">
                          {(jobProgress[job.id].percentComplete ?? 0).toFixed(1)}%
                        </span>
                        <span className="job-progress-counts">
                          {(jobProgress[job.id].processedRecords ?? 0).toLocaleString()} / {(jobProgress[job.id].totalRecords ?? 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="job-progress-times">
                        <span>Started {fmtJobDate(jobProgress[job.id].startedAt ?? null)}</span>
                        <span>Elapsed {formatDuration(jobProgress[job.id].elapsedSeconds)}</span>
                        <span>ETA {formatDuration(jobProgress[job.id].estimatedSecondsRemaining)}</span>
                      </div>
                    </div>
                  )}
                </div>
                <button className="job-run-btn"
                        disabled={job.running}
                        onClick={() => runJob(job.id)}>
                  {job.running ? (
                    <span className="job-spinner">⟳</span>
                  ) : '▶ Run'}
                </button>
              </div>
            ))}
          </div>

          {jobOutput && (
            <div className="job-output-panel">
              <div className="job-output-header">
                <span>Output: {jobOutput.job}</span>
                <button onClick={() => setJobOutput(null)}>✕</button>
              </div>
              <pre className="job-output-body">{jobOutput.output}</pre>
            </div>
          )}
        </div>
      )}

      {/* ── CONFIG ── */}
      {tab === 'config' && (
        <div className="admin-section">
          {Object.entries(configsByCategory).map(([category, cfgs]) => {
            const meta = CATEGORY_META[category] || { label: category, icon: '⚙', description: '' };
            const collapsed = collapsedCategories.has(category);
            return (
              <div key={category} className="config-category">
                <button className="config-category-header" onClick={() => toggleCategory(category)}>
                  <span className="config-category-icon">{meta.icon}</span>
                  <span className="config-category-label">{meta.label}</span>
                  {meta.description && <span className="config-category-desc">{meta.description}</span>}
                  <span className="config-category-count">{cfgs.length} setting{cfgs.length !== 1 ? 's' : ''}</span>
                  <span className="config-category-chevron">{collapsed ? '▶' : '▼'}</span>
                </button>
                {!collapsed && (
                  <div className="config-rows">
                    {cfgs.map(cfg => (
                      <div key={cfg.key} className="config-row">
                        <div className="config-key-col">
                          <div className="config-key">{cfg.key}</div>
                          <div className="config-desc">{cfg.description}</div>
                        </div>
                        {editConfig?.key === cfg.key ? (
                          <div className="config-edit-col">
                            {(() => {
                              const field = CONFIG_FIELDS[cfg.key];
                              if (field?.type === 'multiselect') return (
                                <div style={{flex:1}}>
                                  <MultiSelectBadgeEditor
                                    value={editConfig.value}
                                    options={field.options!}
                                    onChange={v => setEditConfig({...editConfig, value: v})}
                                  />
                                </div>
                              );
                              if (field?.type === 'dropdown') return (
                                <select className="config-input config-select"
                                        value={editConfig.value}
                                        onChange={e => setEditConfig({...editConfig, value: e.target.value})}>
                                  {field.options!.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              );
                              if (field?.type === 'boolean') return (
                                <select className="config-input config-select"
                                        value={editConfig.value}
                                        onChange={e => setEditConfig({...editConfig, value: e.target.value})}>
                                  <option value="true">true</option>
                                  <option value="false">false</option>
                                </select>
                              );
                              if (field?.type === 'sets') return (
                                <div style={{flex:1}}>
                                  <DownloadSetsEditor
                                    value={editConfig.value}
                                    onChange={v => setEditConfig({...editConfig, value: v || 'ALL_SETS'})}
                                  />
                                </div>
                              );
                              return (
                                <input className="config-input"
                                       value={editConfig.value}
                                       onChange={e => setEditConfig({...editConfig, value: e.target.value})}
                                       onKeyDown={e => { if (e.key==='Enter') saveConfig(); if (e.key==='Escape') setEditConfig(null); }}
                                       autoFocus />
                              );
                            })()}
                            <button className="config-save-btn" onClick={saveConfig}>Save</button>
                            <button className="config-cancel-btn" onClick={() => setEditConfig(null)}>✕</button>
                          </div>
                        ) : (
                          <div className="config-value-col">
                            <span className="config-value">{cfg.value}</span>
                            {cfg.editable && (
                              <button className="config-edit-btn"
                                      onClick={() => setEditConfig({ key: cfg.key, value: cfg.value })}>
                                ✎
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── USERS ── */}
      {tab === 'users' && (
        <div className="admin-section">
          <div className="users-header">
            <button className="admin-add-btn" onClick={() => navigate('/admin/users/new')}>
              + New User
            </button>
          </div>
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th><th>Name</th><th>Username</th><th>Email</th>
                <th>DOB</th><th>Role</th><th>Access</th>
                <th>Logins</th><th>Last Login</th><th>Joined</th><th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.userId}>
                  <td>{u.userId}</td>
                  <td>
                    <div className="user-fullname">{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</div>
                  </td>
                  <td><span className="user-username">{u.username}</span></td>
                  <td className="user-email">{u.email}</td>
                  <td className="user-dob">{formatDob(u.dateOfBirth)}</td>
                  <td><span className={`role-badge ${u.role.toLowerCase()}`}>{u.role}</span></td>
                  <td>
                    <span className={`active-badge ${u.active?'yes':'no'}`}>
                      {u.active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td className="user-login-count">{u.loginCount || 0}</td>
                  <td className="user-last-login">
                    {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}
                  </td>
                  <td className="user-last-login">
                    {u.createdDate ? new Date(u.createdDate).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <button className="user-edit-btn"
                            onClick={() => navigate(`/admin/users/${u.userId}/edit`)}>✎</button>
                    <button className="user-delete-btn"
                            onClick={() => deleteUser(u.userId, u.username)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── KEYWORDS ── */}
      {tab === 'keywords' && (
        <div className="admin-section">
          <div className="kw-toolbar">
            <input
              className="kw-search"
              placeholder="Search keywords or definitions…"
              value={kwSearch}
              onChange={e => setKwSearch(e.target.value)}
            />
            <div className="kw-filters">
              {(['active','inactive','all'] as const).map(f => (
                <button key={f}
                        className={`kw-filter-btn ${kwFilter===f?'active':''}`}
                        onClick={() => setKwFilter(f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <select
              className="kw-source-select"
              value={kwSourceFilter}
              onChange={e => setKwSourceFilter(e.target.value)}
            >
              {SOURCES.map(s => (
                <option key={s} value={s}>
                  {s === 'all' ? 'All Sources' : s.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <span className="kw-count">{filteredKeywords.length} / {keywords.length}</span>
          </div>

          {editKw && (
            <div className="kw-edit-modal">
              <div className="kw-edit-card">
                <div className="kw-edit-header">
                  <span className="kw-edit-title">{editKw.keyword}</span>
                  <span className={`kw-source-badge kw-source-${editKw.source}`}>{editKw.source}</span>
                </div>
                <textarea
                  className="kw-edit-textarea"
                  value={editKw.definition}
                  onChange={e => setEditKw({...editKw, definition: e.target.value})}
                  rows={4}
                />
                {editKw.originalDefinition !== editKw.definition && (
                  <div className="kw-original">
                    <span className="kw-original-label">Original:</span>
                    <span className="kw-original-text">{editKw.originalDefinition}</span>
                  </div>
                )}
                <div className="kw-edit-actions">
                  {editKw.originalDefinition !== editKw.definition && (
                    <button className="kw-revert-btn"
                            onClick={() => revertKeyword(editKw)}>↩ Revert to Original</button>
                  )}
                  <button className="kw-cancel-btn" onClick={() => setEditKw(null)}>Cancel</button>
                  <button className="kw-save-btn" onClick={saveKeyword} disabled={kwSaving}>
                    {kwSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="kw-table-wrap">
            <table className="kw-table">
              <thead>
                <tr>
                  <th className="kw-th-sortable" onClick={() => handleKwSort('keyword')}>
                    Keyword{sortIcon('keyword')}
                  </th>
                  <th className="kw-th-sortable" onClick={() => handleKwSort('definition')}>
                    Definition{sortIcon('definition')}
                  </th>
                  <th className="kw-th-sortable" onClick={() => handleKwSort('source')}>
                    Source{sortIcon('source')}
                  </th>
                  <th className="kw-th-sortable" onClick={() => handleKwSort('status')}>
                    Status{sortIcon('status')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredKeywords.map(kw => (
                  <tr key={kw.id} className={kw.active ? '' : 'kw-inactive'}>
                    <td className="kw-name">{kw.keyword}</td>
                    <td className="kw-def">
                      {kw.definition}
                      {kw.originalDefinition !== kw.definition && (
                        <span className="kw-modified-badge">modified</span>
                      )}
                    </td>
                    <td>
                      <span className={`kw-source-badge kw-source-${kw.source}`}>{kw.source}</span>
                    </td>
                    <td>
                      <span className={`kw-status-badge ${kw.active ? 'active' : 'inactive'}`}>
                        {kw.active ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="kw-actions">
                      <button className="kw-edit-btn" onClick={() => setEditKw(kw)}>✎</button>
                      <button
                        className={`kw-toggle-btn ${kw.active ? 'hide' : 'show'}`}
                        onClick={() => toggleKeyword(kw)}
                        title={kw.active ? 'Hide keyword' : 'Show keyword'}>
                        {kw.active ? '👁' : '🚫'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
